import {
  IExperienceTemplateResource,
  ISubTaskDocumentReadTemplate,
  StorageContext
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { CS } from '@dadohr/shared/versions';
import { useTheme } from '@emotion/react';
import {
  FormControlLabel,
  FormHelperText,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Switch,
  TextField,
  Tooltip
} from '@material-ui/core';
import { FileText, HelpCircle, Trash2 } from 'lucide-react';
import { SetStateAction, useState } from 'react';
import shortid from 'shortid';
import { Dropzone } from '../../../../../../../../components/Dropzone';
import {
  FormRow,
  FormRowGroup
} from '../../../../../../../../components/Form/FormRow';
import { TokenDefinition } from '../../../../../../../../components/MarkdownText';
import {
  PdfDialog,
  PDF_VIEWER_DEFAULT_KEYMAP
} from '../../../../../../../../components/PdfDialog';
import { useDialogState } from '../../../../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../../../../i18next';
import { FlexContainer } from '../../../../../../../../layout/Flex';
import { formatBytes, ONE_MB } from '../../../../../../../../services/bytes';
import { readFileAsUint8Array } from '../../../../../../../../services/file';
import { pdfjs } from '../../../../../../../../services/pdfjs';
import {
  ICloudStorageFile,
  storeAllFiles
} from '../../../../../../../../services/storage';
import { BlockSettingsSubPage, TaskBuilderContext } from '../../../types';
import {
  TaskBuilderBlockValidationState,
  TaskBuilderBlockValidationStateItem,
  useFieldValidator,
  VALIDATORS_DOCUMENT_READ
} from '../../../validation';
import { OnCompleteHooks, OnCompleteHooksReadOnly } from './onCompleteHooks';

const SUPPORTED_FILE_TYPES = [
  {
    contentType: 'application/pdf',
    label: 'PDF'
  }
];

const MAX_FILE_SIZE_MB = 20;

const toDocumentStoragePath = (orgId: string, name: string) =>
  `${CS.documents}/${orgId}/${name}`;

const processFile = async (
  orgId: string,
  f: File
): Promise<{
  originalFileName: string;
  docStoragePath: string;
  docUrl: string;
  thumbnailStoragePath: string;
  thumbnailUrl: string;
  contentType: 'application/pdf';
  pages: number;
  sizeInBytes: number;
}> => {
  // Only pdfs for now - don't bother about making it more dynamic etc
  if (f.type !== 'application/pdf') {
    throw new Error('UNSUPPORTED_FILE_TYPE');
  }
  const byteArray = await readFileAsUint8Array(f);
  const doc = await pdfjs().then((p) => p.getDocument(byteArray).promise);
  const page = await doc.getPage(1);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('NO_CANVAS_CONTEXT');
  }
  const viewport = page.getViewport({ scale: 0.5 });
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  await page.render({ canvasContext: ctx, viewport }).promise;
  const thumbnailData = canvas.toDataURL('image/jpeg');
  const thumbnailBlob = await (await fetch(thumbnailData)).blob();

  const docId = shortid();
  const docFile: ICloudStorageFile = {
    name: toDocumentStoragePath(orgId, docId),
    data: f,
    contentType: f.type,
    metadata: {
      orgId,
      originalFileName: f.name,
      numPages: `${doc.numPages}`
    }
  };
  const thumbnailFile: ICloudStorageFile = {
    name: toDocumentStoragePath(orgId, `${docId}-thumbnail`),
    data: thumbnailBlob,
    contentType: 'image/jpeg',
    metadata: {
      orgId,
      thumbnailFor: docFile.name
    }
  };

  const [docRef, thumbnailRef] = await storeAllFiles([docFile, thumbnailFile]);
  const docUrl = await docRef.getDownloadURL();
  const thumbnailUrl = await thumbnailRef.getDownloadURL();
  return {
    originalFileName: f.name,
    docStoragePath: docFile.name,
    docUrl,
    thumbnailStoragePath: thumbnailFile.name,
    thumbnailUrl,
    contentType: 'application/pdf',
    pages: doc.numPages,
    sizeInBytes: f.size
  };
};

export const BlockSettingsDocumentRead = ({
  value,
  onChange,
  validation,
  setValidation,
  expTpl,
  customFields,
  resources,
  onChangeResources,
  goToSubPage,
  showOnCompleteHookSettings,
  tokens
}: {
  value: ISubTaskDocumentReadTemplate;
  onChange: (nextValue: ISubTaskDocumentReadTemplate) => void;
  validation: TaskBuilderBlockValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<TaskBuilderBlockValidationState>
  >;
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const [uploadError, setUploadError] = useState('');
  const [processing, setProcessing] = useState(false);
  const pdfDialog = useDialogState();

  const validateTitle = useFieldValidator(
    setValidation,
    VALIDATORS_DOCUMENT_READ,
    'title'
  );

  const validateHooks = useFieldValidator(
    setValidation,
    VALIDATORS_DOCUMENT_READ,
    'hooks'
  );
  const invalidTitleReason = validation?.fields.title || null;
  const invalidFileReason = uploadError || validation?.fields.file || null;

  const clearFile = () =>
    onChange({
      ...value,
      data: {
        ...value.data,
        originalFileName: '',
        docStoragePath: '',
        docUrl: '',
        thumbnailStoragePath: '',
        thumbnailUrl: '',
        contentType: 'application/pdf',
        pages: 0,
        sizeInBytes: 0
      }
    });

  const d = value.data;
  const hasFile = d.docUrl && d.thumbnailUrl; // and much more - these arguments all come together though
  const fileSize = formatBytes(d.sizeInBytes, 2);
  const filePages = t('{{ count }} pages', { count: d.pages });

  return (
    <>
      <FormRowGroup>
        <FormRow>
          <TextField
            autoFocus
            label={t('Document Title')}
            fullWidth
            variant="outlined"
            value={value.data.title}
            error={!!invalidTitleReason}
            helperText={invalidTitleReason}
            onBlur={() => validateTitle(value, expTpl)}
            onChange={(ev) => {
              const nextValue = {
                ...value,
                data: {
                  ...value.data,
                  title: ev.target.value
                }
              };
              invalidTitleReason && validateTitle(nextValue, expTpl);
              onChange(nextValue);
            }}
          />
        </FormRow>

        {hasFile ? (
          <>
            <List>
              <ListItem button alignItems="flex-start">
                <ListItemIcon>
                  <FileText size={20} />
                </ListItemIcon>
                <ListItemText
                  primary={d.originalFileName}
                  secondary={`${fileSize} | ${filePages}`}
                  secondaryTypographyProps={{ variant: 'caption' }}
                  onClick={() => pdfDialog.openDialog()}
                />
                <ListItemSecondaryAction>
                  <IconButton onClick={clearFile}>
                    <Trash2 size={16} />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </>
        ) : processing ? (
          <>
            <LinearProgress variant="indeterminate" color="primary" />
            <FormHelperText>{t('Processing your file...')}</FormHelperText>
          </>
        ) : (
          <>
            <Dropzone
              label={t('Upload document')}
              error={!!invalidFileReason}
              onDrop={async (fs) => {
                if (!fs.length) {
                  setUploadError(t('Please choose a file'));
                  return;
                }
                if (fs.length > 1) {
                  setUploadError(t('Please select only one document'));
                  return;
                }
                const f = fs[0];
                if (
                  !SUPPORTED_FILE_TYPES.find((t) => t.contentType === f.type)
                ) {
                  setUploadError(t('Please choose a valid file type'));
                  return;
                }

                if (f.size > MAX_FILE_SIZE_MB * ONE_MB) {
                  setUploadError(
                    t('Please choose a file smaller than {{ maxFileSize }}', {
                      maxFileSize: `${MAX_FILE_SIZE_MB}MB`
                    })
                  );
                  return;
                }

                setProcessing(true);
                // create a thumbnail, upload the file, get urls
                try {
                  const result = await processFile(expTpl.orgId, f);
                  onChange({
                    ...value,
                    data: {
                      ...value.data,
                      ...result
                    }
                  });
                  setUploadError('');
                } catch (err: any) {
                  if (err?.message === 'UNSUPPORTED_FILE_TYPE') {
                    setUploadError(t('Please choose a valid file type'));
                  } else {
                    setUploadError(t('Failed to upload file'));
                  }
                }
                setProcessing(false);
              }}
            />
            <FormHelperText error={!!invalidFileReason}>
              {t('Max {{ maxFileSize }}', {
                maxFileSize: `${MAX_FILE_SIZE_MB}MB`
              })}
              {' • '}
              {t('Supported formats')}:{' '}
              {SUPPORTED_FILE_TYPES.map((t) => t.label).join(', ')}
            </FormHelperText>
            {invalidFileReason && (
              <FormHelperText error={true}>{invalidFileReason}</FormHelperText>
            )}
          </>
        )}

        <FormRow>
          <FormControlLabel
            control={
              <Switch
                checked={value.completeOnDownload}
                onChange={() =>
                  onChange({
                    ...value,
                    completeOnDownload: !value.completeOnDownload
                  })
                }
                color="primary"
              />
            }
            label={
              <FlexContainer alignItems="center">
                {t('Complete on download')}
                <Tooltip title="Ask users to download the document, instead of reading it inside of Dado">
                  <HelpCircle size={16} color={theme.custom.colors.gray.w500} />
                </Tooltip>
              </FlexContainer>
            }
          />
        </FormRow>

        {pdfDialog.dialogOpen && (
          <PdfDialog
            open={true}
            onClose={() => pdfDialog.closeDialog()}
            fullScreen
            title={d.title}
            file={{
              url: d.docUrl
            }}
            keyMap={PDF_VIEWER_DEFAULT_KEYMAP}
          />
        )}
      </FormRowGroup>
      <OnCompleteHooks
        value={value.hooks.onComplete}
        tokens={tokens}
        onChange={(nextHooks) => {
          const nextValue = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          validateHooks(nextValue, expTpl);
          onChange(nextValue);
        }}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.SUBTASK}
      />
    </>
  );
};

export const BlockSettingsDocumentReadReadOnly = ({
  value,
  showOnCompleteHookSettings,
  ...props
}: {
  value: ISubTaskDocumentReadTemplate;
  resources: IExperienceTemplateResource[];
  expTpl: TaskBuilderContext;
  customFields: CustomUserField[];
  showOnCompleteHookSettings: boolean;
  tokens: TokenDefinition[];
}) => {
  // TODO mention when completeOnDownload is true
  return (
    <>
      {showOnCompleteHookSettings && (
        <OnCompleteHooksReadOnly
          value={value.hooks.onComplete}
          {...props}
          storageContext={StorageContext.SUBTASK}
        />
      )}
    </>
  );
};

/*

{
    "originalFileName": "buchbinder-harz-2021.pdf",
    "docStoragePath": "documentsV1/DEV_APP/64-vw73_c",
    "docUrl": "https://firebasestorage.googleapis.com/v0/b/emplexp-dev.appspot.com/o/documentsV1%2FDEV_APP%2F64-vw73_c?alt=media&token=c62f179c-f077-41fb-9acc-dedb35b6a9d0",
    "thumbnailStoragePath": "documentsV1/DEV_APP/64-vw73_c-thumbnail",
    "thumbnailUrl": "https://firebasestorage.googleapis.com/v0/b/emplexp-dev.appspot.com/o/documentsV1%2FDEV_APP%2F64-vw73_c-thumbnail?alt=media&token=ca125b78-1d0b-4387-b58c-924de63ad1f4",
    "contentType": "application/pdf",
    "pages": 2,
    "sizeInBytes": 282287
}


*/
